import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Heart, Store } from 'lucide-react';
import { apiFetch } from '../../pages/marketplace/api';
import { ShelfPills } from './ShelfRail';

/**
 * The Marketplace header, on every marketplace page.
 *
 * Search, the cart, Saved — plus the platform's shelves as compact labelled pills on every page
 * that is not the home (the home shows the same shelves as artwork tiles instead).
 *
 * Any cart change anywhere fires `window.dispatchEvent(new Event('cart-updated'))`, which this
 * listens for, so the count is right without threading state through five components.
 */
export function MarketplaceHeader({
  search, onSearch, savedCount = 0, onSaved,
  activeShelf = 'all', onShelf, showShelfPills = true,
}) {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [localSearch, setLocalSearch] = useState(search || '');
  const [shelves, setShelves] = useState([]);

  const refresh = useCallback(() => {
    apiFetch('/cart')
      .then((c) => setCount((c.items || []).reduce((s, i) => s + (i.qty || 1), 0)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('cart-updated', refresh);
    return () => window.removeEventListener('cart-updated', refresh);
  }, [refresh]);

  useEffect(() => {
    if (!showShelfPills) return;
    apiFetch('/marketplace/shelves')
      .then((d) => setShelves((d.shelves || []).filter((s) => (s.product_count || 0) > 0)))
      .catch(() => {});
  }, [showShelfPills]);

  useEffect(() => { setLocalSearch(search || ''); }, [search]);

  const submitSearch = (value) => {
    if (onSearch) { onSearch(value); return; }
    navigate(`/dashboard/marketplace?search=${encodeURIComponent(value)}`);
  };

  const pickShelf = (slug) => {
    if (onShelf) { onShelf(slug); return; }
    navigate(slug === 'all'
      ? '/dashboard/marketplace'
      : `/dashboard/marketplace/shelf/${slug}`);
  };

  return (
    <div className="mp-topbar-sticky" data-testid="marketplace-header-wrap">
      <div className="mp-topbar" data-testid="marketplace-header">
        <button className="mp-topbar-brand" onClick={() => navigate('/dashboard/marketplace')}
          data-testid="mp-header-home">
          <Store size={16} />
          <span>Marketplace</span>
        </button>

        <div className="mp-topbar-search">
          <Search size={15} />
          <input
            placeholder="Search products, vendors, courses..."
            value={localSearch}
            onChange={(e) => { setLocalSearch(e.target.value); if (onSearch) onSearch(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(localSearch); }}
            data-testid="mp-header-search"
          />
        </div>

        <button className="mp-topbar-btn" onClick={() => (onSaved ? onSaved() : navigate('/dashboard/marketplace?view=saved'))}
          data-testid="mp-header-saved">
          <Heart size={15} fill={savedCount > 0 ? 'currentColor' : 'none'} />
          <span className="mp-topbar-btn-label">Saved</span>
          {savedCount > 0 && <span className="mp-topbar-badge saved">{savedCount}</span>}
        </button>

        <button className="mp-topbar-btn cart" onClick={() => navigate('/dashboard/marketplace/cart')}
          data-testid="mp-header-cart">
          <ShoppingBag size={15} />
          <span className="mp-topbar-btn-label">Cart</span>
          {count > 0 && <span className="mp-topbar-badge" data-testid="mp-header-cart-count">{count}</span>}
        </button>
      </div>

      {showShelfPills && (
        <ShelfPills shelves={shelves} active={activeShelf} onSelect={pickShelf} />
      )}
    </div>
  );
}

export default MarketplaceHeader;
